import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { map, groupBy, sortBy, isObject } from "lodash";
import { useAuth } from "../useAuth";
import { callApi } from "../../api";

const parseJSON = (x) => {
  let val;
  try {
    val = JSON.parse(x);
  } catch {
    return null;
  }
  return val;
};

const getType = (type) => {
  switch (type) {
    case "boolean":
      return "bool";
    case "number":
      return "decimal";
    case "array":
      return "json";
    case "object":
      return "json";
    case "options":
      return "json";
    default:
      return type;
  }
};

const getNestedInfo = (key, depth = []) => {
  if (!key) return null;
  if (!depth || !depth.length) return null;
  let str = key;
  depth.forEach((x) => {
    str = str.concat(`->${x}`);
  });
  return str;
};

const getValue = (object, depth = [], type) => {
  const defaultObj = isObject(object) ? { ...object } : null;
  if (!defaultObj) return object;
  let temp = defaultObj;
  if (!depth?.length) return object;
  depth.forEach((x) => {
    temp = temp?.[x];
  });
  if (type === "bool" || type === "boolean")
    return temp === "false" ? false : temp === "true" ? true : temp;
  return temp;
};

const getDefaultValue = (schema) => {
  if (schema?.type !== "object") {
    return schema?.default;
  }
  const temp = {};
  const keys = Object.keys(schema?.properties || {});
  keys.forEach((key) => {
    temp[key] = getDefaultValue(schema?.properties?.[key]);
  });
  return temp;
};

const generateSettingsFromSchema = (
  parentId,
  parentKey,
  key,
  schema,
  settingArray,
  value,
  depth = [],
  isRoot = false
) => {
  if (schema?.type !== "object") {
    settingArray.push({
      id: isRoot ? parentId : undefined,
      parentId: isRoot ? undefined : parentId,
      parentKey,
      depth,
      name: key,
      description: schema?.description,
      additionalDesc: getNestedInfo(parentKey, depth),
      type:
        schema?.type === "array"
          ? "array"
          : schema?.type === "options"
          ? "options"
          : getType(schema?.type),
      prettyname: schema?.title,
      value: getValue(value, depth, schema?.type),
      defaultvalue: getDefaultValue(schema),
      category: schema?.category,
      editable: schema?.editable,
      cancopy: schema?.cancopy,
      isunique: schema?.isunique,
      isvisible: schema?.isvisible,
      syncappt: schema?.syncappt,
      reload: schema?.reload,
      options:
        schema?.type === "options"
          ? { type: "options", options: schema?.options || [] }
          : schema?.type === "array"
          ? { type: "array", items: schema?.items || {} }
          : schema?.enum,
    });
  } else {
    const keys = Object.keys(schema.properties || {});
    keys.forEach((el) => {
      generateSettingsFromSchema(
        parentId,
        parentKey,
        el,
        schema.properties[el] || {},
        settingArray,
        value,
        [...depth, el],
        false
      );
    });
  }
};

export default () => {
  const { selectedBusiness, runWithGuard, isAdmin } = useAuth();
  const [businessSettings, setBusinessSettings] = useState(null);
  const [settingsArray, setSettingsArray] = useState(null);
  const [businessSettingsLoading, setBusinessSettingsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const getBusinessSettings = async () => {
    setBusinessSettingsLoading(true);
    runWithGuard(async () => {
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/bizsettings/get`
      );
      if (res && res.status === true) {
        setSettingsArray(res?.data);
        setBusinessSettingsLoading(false);
      } else {
        toast.error(res?.message || "Failed to fetch business settings!");
        setBusinessSettingsLoading(false);
      }
    });
  };

  // POST protected/admin/settingtypes/add => Add
  // POST protected/admin/settingtypes/{sid}/update => Update

  const addSettingType = async (schema) => {
    const data = {
      category: schema.category,
      description: schema.description,
      editable: schema.editable,
      cancopy: schema.cancopy,
      isunique: schema.isunique,
      isvisible: schema.isvisible,
      name: schema.name?.toUpperCase?.(),
      // name: schema.name,
      prettyname: schema.title,
      type: getType(schema.type),
      syncappt: schema.syncappt,
      reload: schema.reload,
      defaultvalue: JSON.stringify(getDefaultValue(schema)) || null,
      options:
        getType(schema.type) === "json"
          ? JSON.stringify(schema)
          : JSON.stringify(parseJSON(schema)?.enum) || null,
    };

    const res = await callApi(
      `/protected/admin/settingtypes/add`,
      "POST",
      data
    );
    if (res && res.status === true) {
      setSettingsArray([...settingsArray, res.data]);
      toast.success("Added Setting Successfully");
      return;
    }
    toast.error((res && res.message) || "Failed to add new setting!");
  };

  const updateSettingType = async (setting, schema) => {
    const data = {
      category: schema.category,
      description: schema.description,
      editable: schema.editable,
      cancopy: schema.cancopy,
      isunique: schema.isunique,
      isvisible: schema.isvisible,
      name: schema.name?.toUpperCase?.(),
      // name: schema.name,
      prettyname: schema.title,
      type: getType(schema.type),
      syncappt: schema.syncappt,
      reload: schema.reload,
      defaultvalue: getDefaultValue(schema)
        ? (typeof getDefaultValue(schema) === "string" ? getDefaultValue(schema) : JSON.stringify(getDefaultValue(schema)))
        : setting.default || null,
      options:
        getType(schema.type) === "json"
          ? (typeof schema === "string" ? schema : JSON.stringify(schema))
          : (typeof schema?.enum === "string" ? schema?.enum : JSON.stringify(schema?.enum)) || null,
    };

    const res = await callApi(
      `/protected/admin/settingtypes/${setting.id}/update`,
      "POST",
      data
    );
    if (res && res.status === true) {
      const array = settingsArray;
      setSettingsArray(
        array.map((el) => {
          if (el.id === res.data.id) return { ...res.data, value: el.value };
          return el;
        })
      );
      toast.success("Setting Updated Successfully");
      return;
    }
    toast.error((res && res.message) || "Failed to update setting!");
  };

  useEffect(() => {
    if (settingsArray) {
      const temp = settingsArray
        .flatMap((el) => {
          const settingArray = [];
          if (el.type !== "json") {
            settingArray.push(el);
            return settingArray;
          }
          const schema = parseJSON(el.options);
          if (!el.options || !schema) {
            settingArray.push(el);
            return settingArray;
          }
          if (schema) {
            const keys = Object.keys(schema.properties || {});
            if (!keys.length) {
              settingArray.push(el);
              return settingArray;
            }
          }
          generateSettingsFromSchema(
            el.id,
            el.name, // parent key
            el.name || schema.name,
            schema,
            settingArray,
            el.value,
            [],
            true
          );
          return settingArray;
        })
        .map((x) => ({ ...x, category: x.category || "Misc. Settings" }));

      const allSettings = sortBy(
        map(groupBy(temp, "category"), (v, k) => ({
          key: k === "null" || !k ? "Misc. Settings" : k,
          value: v,
          order: (v && v.length && v[0] && v[0].order) || 1000,
        })),
        ["order", "key"]
      );

      const _temp = [
        {
          key: "Business Hours",
          order: 2,
          value: [
            {
              day: "sunday",
              start: null,
              end: null,
              closed: false,
              isvisible: 2,
            },
            {
              day: "monday",
              start: null,
              end: null,
              closed: false,
              isvisible: 2,
            },
            {
              day: "tuesday",
              start: null,
              end: null,
              closed: false,
              isvisible: 2,
            },
            {
              day: "wednesday",
              start: null,
              end: null,
              closed: false,
              isvisible: 2,
            },
            {
              day: "thursday",
              start: null,
              end: null,
              closed: false,
              isvisible: 2,
            },
            {
              day: "friday",
              start: null,
              end: null,
              closed: false,
              isvisible: 2,
            },
            {
              day: "saturday",
              start: null,
              end: null,
              closed: false,
              isvisible: 2,
            },
          ],
        },
        ...(allSettings?.find((x) => x.key === "Business Settings")
          ? []
          : [{ key: "Business Settings", order: 3, value: [] }]),
        ...allSettings,
      ];

      setBusinessSettings(_temp.map(s => {
        return {
          ...s,
          value: s?.value?.filter(val => {
            const isvisible = val?.isvisible || null;
            if ((!isvisible && isvisible !== 0) || isvisible === 1) return isAdmin;
            if (isvisible === 2) return true;
            return false;
          })
        }
      }) || []);
    }
  }, [settingsArray]);

  return {
    setRefresh,
    getBusinessSettings,
    businessSettings,
    settingsArray,
    setSettingsArray,
    setBusinessSettings,
    refresh,
    businessSettingsLoading,
    addSettingType,
    updateSettingType,
  };
};
